import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../contexts/language/LanguageContext';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useSEO } from '../../hooks/useSEO';
import { aboutImages } from '../../helpers/images.helper';
import { INTERSECTION_THRESHOLDS, SWIPE_THRESHOLDS } from '../../helpers/constants.helper';
import Skeleton from '../../components/skeleton/Skeleton';
import './AboutPage.scss';

const AboutPage = () => {
  const { t } = useLanguage();
  const [intersectionRef, isVisible] = useIntersectionObserver({
    threshold: INTERSECTION_THRESHOLDS.HIGH
  });

  // SEO optimization
  useSEO({
    title: 'About - ALINA POLONEYEVA',
    description: 'Learn about Alina Poloneyeva - Ukrainian visual creator, model, and fashion week presence. Professional background in editorial modeling and fashion content creation. Based in Warsaw and Paris.',
    keywords: 'Alina Poloneyeva about, Ukrainian model biography, visual creator background, fashion model profile, Ukrainian fashion model, Warsaw model, Paris model',
    ogTitle: 'About Alina Poloneyeva - Visual Creator & Model',
    ogDescription: 'Ukrainian visual creator, model, and fashion week presence',
    ogImage: 'https://alinapoloneyeva.com/images/about/about.jpg',
    ogUrl: 'https://alinapoloneyeva.com/about',
    canonical: 'https://alinapoloneyeva.com/about',
  });
  const [activeImage, setActiveImage] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>(new Array(aboutImages.length).fill(false));
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const isSwiping = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleImageLoad = (index: number) => {
    setImagesLoaded(prev => {
      const newState = [...prev];
      newState[index] = true;
      return newState;
    });
  };

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      touchEndX.current = e.touches[0].clientX;
      isSwiping.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      touchEndX.current = e.touches[0].clientX;

      // Запобігаємо горизонтальній навігації браузера при свайпі
      const diffX = Math.abs(touchStartX.current - touchEndX.current);
      if (diffX > SWIPE_THRESHOLDS.MIN_MOVEMENT) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      const swipeDistance = Math.abs(touchStartX.current - touchEndX.current);

      if (swipeDistance > SWIPE_THRESHOLDS.SWIPE_DISTANCE) {
        isSwiping.current = true;

        if (touchStartX.current - touchEndX.current > SWIPE_THRESHOLDS.SWIPE_DISTANCE) {
          // Swipe left - next image
          setActiveImage((prev) => (prev + 1) % aboutImages.length);
        } else if (touchStartX.current - touchEndX.current < -SWIPE_THRESHOLDS.SWIPE_DISTANCE) {
          // Swipe right - previous image
          setActiveImage((prev) => (prev - 1 + aboutImages.length) % aboutImages.length);
        }
      }
    };

    wrapper.addEventListener('touchstart', handleTouchStart);
    wrapper.addEventListener('touchmove', handleTouchMove, { passive: false });
    wrapper.addEventListener('touchend', handleTouchEnd);

    return () => {
      wrapper.removeEventListener('touchstart', handleTouchStart);
      wrapper.removeEventListener('touchmove', handleTouchMove);
      wrapper.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    // Не перемикати якщо клікнули на індикатор або був свайп
    if (isSwiping.current) {
      isSwiping.current = false;
      return;
    }

    const target = e.target as HTMLElement;
    if (target.closest('.about__portrait-indicators')) {
      return;
    }

    // Click - next image
    setActiveImage((prev) => (prev + 1) % aboutImages.length);
  };

  return (
    <section className="about" ref={intersectionRef}>
      <div className={`about__container ${isVisible ? 'visible' : ''}`}>
        <h2 className="about__title">{t.about.title}</h2>

        <div className="about__grid">
          <div className="about__portrait">
            <div
              ref={wrapperRef}
              className="about__portrait-wrapper"
              onClick={handleClick}
            >
              {activeImage !== null && !imagesLoaded[activeImage] && (
                <div className="about__portrait-skeleton">
                  <Skeleton variant="image" width="100%" height="100%" />
                </div>
              )}
              {aboutImages.map((image, index) => (
                <img
                  key={index}
                  src={image.src}
                  alt={image.alt}
                  loading="lazy"
                  className={`about__portrait-image ${activeImage === index ? 'active' : ''} ${imagesLoaded[index] ? 'loaded' : ''}`}
                  onLoad={() => handleImageLoad(index)}
                />
              ))}

              <div className="about__portrait-indicators">
                {aboutImages.map((_, index) => (
                  <button
                    key={index}
                    className={`about__portrait-indicator ${activeImage === index ? 'active' : ''}`}
                    onClick={() => setActiveImage(index)}
                    aria-label={`View image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="about__text">
            <div className="about__content">
              <p>{t.about.paragraph1}</p>
              <p>{t.about.paragraph2}</p>
              <p>{t.about.paragraph3}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutPage;